import React from "react";
import { Phone } from "lucide-react";
import { cn } from "../../lib/utils";
import { formatPhoneDisplay } from "../../lib/phone-formatter";
import { type BaseComponentProps } from "../../types/puck";
import { type JobsiteBusinessContext } from "../../types/runtime-config";

/**
 * Says the business takes emergency work, and gives one way to act on it.
 *
 * Deliberately NOT "24/7". The stored fact is `Business.emergencyService`,
 * which means the business takes emergencies — not that anyone is awake at
 * 3am. Writing 24/7 here would be a claim nothing backs.
 *
 * It is a normal in-flow section rather than a fixed bar because jobsites are
 * prerendered: a fixed bar gated on a client effect is absent from the HTML a
 * crawler or an answer engine reads, which for a safety-relevant claim is the
 * whole point of having one.
 */

export const EMERGENCY_BAND_DEFAULT_MESSAGE = "Emergency service available.";

export interface EmergencyBandProps extends BaseComponentProps {
  /** Editable. Everything else about this band comes from the business. */
  message?: string;
  emergencyService?: boolean | null;
  phone?: string;
  /** Renders an explanatory placeholder instead of nothing, in the editor only. */
  isEditing?: boolean;
  /** A transparent header is fixed with no spacer, so the first section clears it. */
  navFixed?: boolean;
}

/**
 * The number to call, or null when there is nothing to claim.
 *
 * Strict `=== true` on purpose: the column is `boolean | null`, and null means
 * the owner has never answered, which is not the same as yes.
 */
export function resolveEmergencyCall(
  business: Pick<JobsiteBusinessContext, "emergencyService" | "emergencyPhone"> | undefined,
  fallbackPhone: string | undefined,
): { phone: string } | null {
  if (business?.emergencyService !== true) {
    return null;
  }
  const phone = business.emergencyPhone?.trim() || fallbackPhone?.trim();
  return phone ? { phone } : null;
}

export const EmergencyBand: React.FC<EmergencyBandProps> = ({
  id,
  className,
  message,
  emergencyService,
  phone,
  isEditing,
  navFixed,
}) => {
  const call = resolveEmergencyCall({ emergencyService, emergencyPhone: phone }, undefined);

  if (!call) {
    if (!isEditing) {
      return <></>;
    }
    return (
      <section
        id={id}
        className={cn(
          "jobsite:border jobsite:border-dashed jobsite:border-border jobsite:bg-muted/40 jobsite:px-6 jobsite:py-4 jobsite:text-center jobsite:text-sm jobsite:text-muted-foreground",
          className,
        )}
      >
        Appears once emergency service is turned on in Business settings.
      </section>
    );
  }

  return (
    <section
      id={id}
      aria-label="Emergency service"
      className={cn(
        "jobsite:bg-primary jobsite:text-primary-foreground",
        navFixed && "jobsite:pt-16",
        className,
      )}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:flex jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:gap-3 jobsite:px-4 jobsite:py-4 jobsite:text-center jobsite:sm:flex-row jobsite:sm:gap-6">
        <p className="jobsite:m-0 jobsite:font-heading jobsite:text-base jobsite:font-semibold">
          {/* Tolerate a ReactNode in `message`: the editors swap the string
              for an inline-editable element. Strings keep the exact old
              semantics (blank falls back to the default). */}
          {typeof message === "string" || message == null
            ? message?.trim() || EMERGENCY_BAND_DEFAULT_MESSAGE
            : message}
        </p>
        <a
          href={`tel:${call.phone.replace(/\D/g, "")}`}
          data-slot="button"
          data-track="call"
          data-track-label="emergency-band"
          className="jobsite:inline-flex jobsite:items-center jobsite:gap-2 jobsite:bg-primary-foreground jobsite:px-5 jobsite:py-2 jobsite:font-semibold jobsite:text-primary jobsite:transition-opacity jobsite:hover:opacity-90"
          style={{ borderRadius: "var(--jobsite-radius)" }}
        >
          <Phone className="jobsite:h-4 jobsite:w-4" />
          Call {formatPhoneDisplay(call.phone)}
        </a>
      </div>
    </section>
  );
};
